import { LogoMark } from '@/components/LogoMark';
import { SplashScreen } from '@/components/SplashScreen';
import { ButtonLink } from '@/components/ui/Button';
import { ImpersonateMenu } from '@/features/auth/ImpersonateMenu';
import { useAuth } from '@/features/auth/useAuth';
import { AttentionBell } from '@/features/notifications/AttentionPopover';
import { GlobalSearch } from '@/features/search/GlobalSearch';
import { GlobalSearchMobile } from '@/features/search/GlobalSearchMobile';
import { ThemeToggle } from '@/features/theme/ThemeToggle';
import type { UserRole } from '@coreltb/shared/constants';
import { ChevronLeft, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MobileSidebar } from './MobileSidebar';
import { MobileTabBar } from './MobileTabBar';
import { UserMenu } from './UserMenu';
import { useMobileBack } from './mobileBack';

const CAN_CREATE_LEAD: readonly UserRole[] = [
  'admin',
  'developer',
  'dyrektor_sprzedazy',
  'marketing',
  'handlowiec',
  'kierownik',
];

/**
 * Pasek gorny (redesign 2026-09-30).
 * Telefon: znak firmy, szukaj, powiadomienia, profil; nawigacja w dolnym pasku.
 * Desktop: szukaj (Ctrl+K), „Nowy lead”, wcielenie, motyw, powiadomienia, profil.
 */
export function Topbar() {
  const { user, logout } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const mobileBack = useMobileBack();
  const canCreateLead = Boolean(user && CAN_CREATE_LEAD.includes(user.role));

  return (
    <>
      <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-1 border-b border-border bg-surface/90 px-2 backdrop-blur-md sm:gap-2 sm:px-4 lg:h-16 lg:px-6">
        {mobileBack ? (
          <Link
            to={mobileBack.to}
            aria-label={`Wróć: ${mobileBack.label}`}
            className="flex h-11 min-w-0 max-w-[60vw] shrink-0 items-center gap-0.5 rounded-md pl-0.5 pr-3 text-[15px] font-semibold text-fg transition-colors active:bg-surface-2 lg:hidden"
          >
            <ChevronLeft className="h-6 w-6 shrink-0 text-accent" strokeWidth={2.25} />
            <span className="truncate">{mobileBack.label}</span>
          </Link>
        ) : (
          <Link
            to="/crm"
            aria-label="CoreLTB — strona główna"
            className="flex items-center px-1 lg:hidden"
          >
            <LogoMark size="sm" />
          </Link>
        )}

        <div className="flex min-w-0 flex-1 items-center">
          <GlobalSearch />
        </div>

        <GlobalSearchMobile />

        {canCreateLead && (
          <ButtonLink
            to="/crm/nowy"
            variant="primary"
            icon={Plus}
            className="hidden lg:inline-flex"
          >
            Nowy lead
          </ButtonLink>
        )}

        <ImpersonateMenu />
        <div className="hidden lg:block">
          <ThemeToggle />
        </div>
        <AttentionBell />
        <UserMenu onLogout={() => setLoggingOut(true)} />
      </header>

      <MobileTabBar onOpenMenu={() => setMenuOpen(true)} />
      <MobileSidebar open={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* Splash wylogowania (2.5 s) — potem logout() robi soft-logout + reload. */}
      {loggingOut && <SplashScreen durationMs={2500} onComplete={logout} status="Wylogowywanie…" />}
    </>
  );
}
